import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChatComposer } from "@altek/ui/components/chat/ChatComposer";
import { ChatPanel } from "@altek/ui/components/chat/ChatPanel";
import { ApiError } from "@altek/ui/lib/problem";
import { toast } from "@altek/ui/lib/utils/toast";
import { useAuth } from "@/app/providers/AuthProvider";
import { isRestrictedSupportSession } from "@/lib/auth/support-session";
import { personQuery } from "@/features/directory/api/directory.queries";
import { chatApi } from "../api/chat.api";
import { CHAT_PERMISSIONS } from "../api/chat.types";
import { invalidateChat } from "../lib/chat-invalidate";
import { chatErrorMessage, isConversationArchivedError } from "../lib/chat-errors";
import { localId } from "../lib/chat-ids";
import { DRAFT_FALLBACK_NAME, type DraftHandoff } from "../lib/chat-draft";
import { composerBlockReason, personPhotoUrl } from "../lib/chat-map";

/**
 * HENÜZ VAR OLMAYAN KONUŞMA.
 *
 * NEDEN. «Canlı yardım» düğmesi konuşmayı ANINDA açıyordu: `POST /chat/conversations` bir satır
 * yaratıyor, kişi hiçbir şey yazmadan vazgeçse bile o satır listede kalıyordu. Kullanıcının
 * gördüğü şey buydu — bastığı her düğme, sohbet listesine boş bir konuşma bırakıyor.
 *
 * Bu yüzey o satırı YAZILANA KADAR açmıyor: ekranda konuşma varmış gibi duruyor (karşı tarafın
 * adı, avatarı, yazma alanı), ama sunucuda hiçbir şey yok. İlk mesajla birlikte iki iş sırayla
 * yapılıyor — önce konuşma açılıyor, sonra mesaj ona gönderiliyor — ve ekran gerçek konuşmaya
 * devrediliyor. Yazmadan kapatan kişi arkasında iz bırakmıyor.
 *
 * BİREBİR SOHBETİN TEK GİRİŞİ (denetim Sohbet 18). Canlı yardım, rehberin «Sohbet başlat»'ı,
 * yeni sohbet bölmesinde kişi seçmek ve sayfanın `?with=` adresi — hepsi buradan geçiyor. Önceden
 * bunların üçü konuşmayı anında açıyordu ve karşı tarafın listesine boş bir konuşma düşüyordu.
 *
 * ADI REHBERDEN geliyor (`GET /directory/people/{id}`), oturumdan değil: oturum yalnız destek
 * kişisinin KİMLİĞİNİ taşıyor, adını değil. Çağıran adı biliyorsa (rehber kartı, kişi seçici)
 * `nameHint` ile veriyor; ikisi de yoksa başlık «Yeni sohbet» — boş bir başlıktan iyidir ve
 * yanlış bir ad değildir.
 *
 * YAZMA KUTUSU KONUŞMANINKİYLE AYNI KURALLA KAPANIYOR (`composerBlockReason`): destek oturumunda
 * ve gönderme anahtarı yokken kutu nedeniyle birlikte kapalı. Sohbet 18'den sonra her birebir
 * başlangıç bu yüzeyden geçiyor; kural burada yoktu ve destek oturumundaki yönetici bütün mesajı
 * yazıp ancak gönderince 403 alıyordu (bağımsız inceleme).
 *
 * ÇAĞIRAN BİR KİŞİYE BİR ÖRNEK VERMELİ (`key={userId}`): aynı örnek başka bir kişiye geçerse
 * yazılan metin o kişiye taşınır. Gönderim kimliği ayrıca KİŞİYE de bağlı — bkz. `submission`.
 */
export function DraftConversation({
  userId,
  meId,
  onOpened,
  nameHint,
  chrome = "flush",
  pending = false,
  actions,
  autoFocus = false,
}: {
  userId: string;
  meId: string | null;
  /**
   * Gerçek konuşma açıldığında: yüzey artık ona geçiyor. `handoff` varsa mesaj GİTMEDİ (konuşma
   * arşivde) ve yazılan metin o konuşmanın yazma kutusuna taşınmalı.
   */
  onOpened: (conversationId: string, handoff?: DraftHandoff) => void;
  nameHint?: string | undefined;
  /** Sayfada konuşma kendi kutusudur (`framed`); yuvada panelin içinde duruyor (`flush`). */
  chrome?: "framed" | "flush";
  /**
   * Çağıran henüz «bu kişiyle konuşma var mı» sorusunun cevabını bekliyor (liste yükleniyor).
   * Yüzey iskelet çiziyor ve yazma alanı kapalı: cevap «var» çıkarsa taslağa yazılmış metin
   * var olan konuşmaya taşınamaz, kaybolurdu.
   */
  pending?: boolean;
  /** Başlık şeridindeki eylemler (dar ekranda «Sohbetler» dönüşü). */
  actions?: ReactNode;
  /**
   * Yazma kutusu hazır olunca odağı ona ver — BİR KEZ. Sohbet sayfası veriyor: taslak her zaman
   * bir kişinin basışıyla açılıyor (yeni sohbet bölmesinde kişi, rehber, canlı yardım) ve boş
   * bir konuşmada okunacak bir şey yok; yazılacak yer var. Yuva kendi odağını kendisi taşıyor.
   */
  autoFocus?: boolean;
}) {
  const queryClient = useQueryClient();
  const { permissions, session } = useAuth();
  const [text, setText] = useState("");
  const blockReason = composerBlockReason({
    canSend: permissions.can(CHAT_PERMISSIONS.send),
    archived: false,
    impersonating: isRestrictedSupportSession(session),
  });

  const person = useQuery(personQuery(userId));
  const name = person.data?.displayName ?? nameHint ?? DRAFT_FALLBACK_NAME;
  const photo = personPhotoUrl(userId, person.data?.hasPhoto ?? false, person.data?.photoFileId);
  /**
   * REHBERDE GÖRÜNMEYEN KİŞİYE TASLAK YAZDIRILMIYOR. Kart 404 dönüyorsa kişi erişimin dışında ya
   * da kaydı kaldırılmış; yazma kutusu açık kalsaydı ilk mesaj yalnız bir hata bildirimiyle
   * biterdi. Yüzey bunu söylüyor ve yazma kutusunu çizmiyor. Başka bir hata (ağ) yazmayı
   * engellemiyor: sunucu kararı konuşmayı açarken veriyor.
   */
  const unreachable = person.error instanceof ApiError && person.error.status === 404;

  const composerHost = useRef<HTMLDivElement | null>(null);
  const focused = useRef(false);
  const composerReady = !pending && blockReason === null && !unreachable;
  useEffect(() => {
    if (!autoFocus || focused.current || !composerReady) return;
    const field = composerHost.current?.querySelector<HTMLTextAreaElement>("textarea:not([disabled])");
    if (!field) return;
    focused.current = true;
    field.focus({ preventScroll: true });
  }, [autoFocus, composerReady]);

  /**
   * GÖNDERİMİN KİMLİĞİ — mutasyonun DIŞINDA, gönderimin kendisiyle birlikte üretiliyor.
   *
   * §11: anahtar bir İSTEĞİ değil bir GÖNDERİMİ tanımlıyor. Mutasyonun içinde üretilen anahtar
   * her denemede yeniydi: yanıtı ağda düşen gönderim ikinci basışta sunucuda ikinci kez
   * saklanıyordu. Üstelik tarayıcının UUID üreticisi güvensiz origin'de (depoda IP ile açılan makine)
   * yok — konuşma açılıyor, mesaj hiç gitmiyordu. `localId` o durumun yedeğini zaten taşıyor.
   *
   * Anahtar METNE ve KİŞİYE bağlı: aynı kişiye aynı metnin yeniden denenmesi aynı gönderimdir ve
   * aynı anahtarı taşır; metin ya da alıcı değiştiyse bu başka bir gönderimdir ve yeni anahtar
   * alır. Başarıda bırakılıyor. Alıcı şartı bir savunma: sunucu anahtarı konuşma yolu olmadan
   * yalnız gövdeyle eşliyor, yani A'ya düşen gönderimin anahtarı B'ye giderse sunucu B'ye A'nın
   * cevabını yeniden oynatır ve B'ye mesaj hiç gitmez (bağımsız inceleme ölçtü; çağıran ayrıca
   * `key={userId}` veriyor).
   */
  const submission = useRef<{ userId: string; body: string; key: string } | null>(null);

  /**
   * İKİ ADIM, TEK MUTASYON. Konuşmayı açmak ile ilk mesajı göndermek ayrı iki istek ama tek bir
   * kullanıcı eylemi: ikisini ayrı mutasyonlara bölmek, ilki başarılı olup ikincisi düşerse
   * ekranda "gönderildi mi" sorusuna cevabı olmayan bir ara durum bırakırdı. İlk adım yeniden
   * denemede tekrarlanabilir: §14b birebir konuşmada var olanı döndürüyor.
   */
  const send = useMutation({
    mutationFn: async (input: {
      body: string;
      idempotencyKey: string;
    }): Promise<{ conversationId: string; delivered: boolean }> => {
      const conversation = await chatApi.createDirect(userId);
      /**
       * ARŞİVDEKİ BİREBİR SOHBET. Sunucu var olan konuşmayı döndürüyor ama arşivi açmıyor; mesaj
       * 409 ile reddedilirdi. İstek hiç atılmıyor, yüzey gerçek konuşmaya geçiyor ve metin oraya
       * taşınıyor. Karar ile gönderim arasında arşivlenmişse aynı red `catch`'te aynı yola gidiyor.
       */
      if (conversation.archived) return { conversationId: conversation.id, delivered: false };
      try {
        await chatApi.send(conversation.id, { body: input.body }, input.idempotencyKey);
      } catch (error) {
        if (isConversationArchivedError(error)) {
          return { conversationId: conversation.id, delivered: false };
        }
        throw error;
      }
      return { conversationId: conversation.id, delivered: true };
    },
    onSuccess: async ({ conversationId, delivered }, input) => {
      submission.current = null;
      setText("");
      if (delivered) {
        onOpened(conversationId);
      } else {
        onOpened(conversationId, { unsentText: input.body });
        toast.info("Bu kişiyle sohbetiniz arşivde; yazdığınız metin korundu. Göndermek için arşivden çıkarın.");
      }
      await invalidateChat(queryClient);
    },
    onError: (error) => toast.errorMessage("Mesaj gönderilemedi", chatErrorMessage(error)),
  });

  return (
    <ChatPanel
      chrome={chrome}
      title={name}
      kind="direct"
      peer={{ id: userId, name, ...(photo ? { src: photo } : {}) }}
      messages={[]}
      isLoading={pending}
      {...(unreachable
        ? {
            error:
              "Bu kişi rehberde görünmüyor; kaydı kaldırılmış ya da erişiminiz dışında olabilir.",
          }
        : {})}
      {...(actions ? { actions } : {})}
      currentUserId={meId ?? ""}
      composer={
        unreachable ? undefined : (
          <div ref={composerHost}>
            <ChatComposer
              value={text}
              onChange={setText}
              onSend={() => {
                const body = text.trim();
                if (body.length === 0 || send.isPending || pending || blockReason !== null) return;
                if (submission.current?.body !== body || submission.current.userId !== userId) {
                  submission.current = { userId, body, key: localId("snd") };
                }
                send.mutate({ body, idempotencyKey: submission.current.key });
              }}
              isSending={send.isPending}
              disabled={pending || blockReason !== null}
              {...(blockReason ? { disabledReason: blockReason } : {})}
              placeholder="Mesaj yazın…"
            />
          </div>
        )
      }
    />
  );
}
